import { useMemo } from 'react';

import type { RequestError } from '@/services/config';
import { useLocationFishGroupsController_occupiedPens } from '@/services/hooks';

/** A pen an open stay holds, and the group holding it. */
export interface SiteOccupiedPen {
    cageId: number;
    fishGroupId: number;
    fishGroupName: string;
    /** Null for a group in no stocking. */
    stockingCycleId: number | null;
    fishTypeId: number;
}

export interface UseGetSiteOccupancyResult {
    /** Pens a new group can't go into: an open stay holds them, whatever cycle it is in. */
    occupiedCageIds: ReadonlySet<number>;
    /** Names of the live groups holding those pens, pending ones included. */
    groupNames: readonly string[];
    pens: readonly SiteOccupiedPen[];
    isLoading: boolean;
    error: Error | RequestError | null;
    refetch: () => void;
}

/**
 * Which pens of a site are taken, across every cycle and the unassigned groups, a stocking
 * registered for any later date included. The client overview counts only active groups; this
 * is the backend's own rule for refusing a pen.
 */
export const useGetSiteOccupancy = (
    clientId: number,
    locationId: number
): UseGetSiteOccupancyResult => {
    const query = useLocationFishGroupsController_occupiedPens(clientId, locationId);

    const { occupiedCageIds, groupNames, pens } = useMemo(() => {
        const rows = query.data?.data.pens ?? [];
        return {
            occupiedCageIds: new Set(rows.map((pen) => pen.cageId)),
            groupNames: rows.map((pen) => pen.fishGroupName),
            pens: rows.map((pen) => ({
                cageId: pen.cageId,
                fishGroupId: pen.fishGroupId,
                fishGroupName: pen.fishGroupName,
                stockingCycleId: pen.stockingCycleId ?? null,
                fishTypeId: pen.fishTypeId,
            })),
        };
    }, [query.data?.data]);

    return {
        occupiedCageIds,
        groupNames,
        pens,
        isLoading: query.isLoading,
        error: query.error,
        refetch: query.refetch,
    };
};
